/**
 * useFeatureFlag — React hook for checking feature flags (Step 23)
 *
 * Calls the backend feature-flag check endpoint and caches the result
 * in component state.  Returns `{ enabled, loading, error }`.
 *
 * Usage:
 *   const { enabled, loading } = useFeatureFlag('learning_hub');
 */
import { useState, useEffect, useRef } from 'react';
import api from '../api/client';

// Simple in-memory cache so repeated mounts don't re-fetch
const cache = new Map();
const CACHE_TTL = 60_000; // 1 minute

export function useFeatureFlag(flagKey) {
  return useFeatureFlagDefault(flagKey);
}

export default function useFeatureFlagDefault(flagKey) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const fetchFlag = async () => {
      // Check cache first
      const cached = cache.get(flagKey);
      if (cached && Date.now() - cached.ts < CACHE_TTL) {
        setEnabled(cached.enabled);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data } = await api.featureFlags.check(flagKey);
        if (mountedRef.current) {
          const value = !!data.enabled;
          setEnabled(value);
          cache.set(flagKey, { enabled: value, ts: Date.now() });
          setError(null);
        }
      } catch (err) {
        if (mountedRef.current) {
          console.error(`[useFeatureFlag] Failed to check "${flagKey}":`, err);
          setEnabled(false);
          setError(err);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchFlag();

    return () => {
      mountedRef.current = false;
    };
  }, [flagKey]);

  return { enabled, loading, error };
}

// Utility to invalidate all cached flags (e.g. after admin toggles)
export function invalidateFlagCache() {
  cache.clear();
}
